import { useEffect, useState } from "react";
import {
  CheckCircle2,
  Loader2,
  ShieldQuestion,
  Trash2,
  UserPlus,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ExportButton } from "@/components/ExportButton";
import { api, apiError, fmtWhen } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  awaiting: "hsl(36 92% 44%)",
  verified: "hsl(var(--healthy))",
  rejected: "hsl(0 72% 45%)",
  pending_approval: "hsl(36 92% 44%)",
  invited: "hsl(199 89% 48%)",
  active: "hsl(var(--healthy))",
  refused: "hsl(0 72% 45%)",
  deactivated: "hsl(240 5% 55%)",
};

const Chip = ({ value, testId }) => (
  <span
    data-testid={testId}
    className="border px-2 py-0.5 font-display text-[10px] font-bold uppercase tracking-wider"
    style={{
      borderColor: STATUS_TONE[value] || "hsl(240 5% 55%)",
      color: STATUS_TONE[value] || "hsl(240 5% 55%)",
    }}
  >
    {String(value || "").replace("_", " ")}
  </span>
);

const DecisionDialog = ({ open, onOpenChange, item, approve, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const go = async () => {
    setBusy(true);
    try {
      const path = approve ? "verify" : "reject";
      await api.post(`/pending/${item.id}/${path}`, { note });
      toast.success(
        approve
          ? "Verified. The change is now part of the register and the review packs."
          : "Turned down. The register stays as it was.",
      );
      onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card sm:max-w-lg" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold">
            {approve ? "Verify this change" : "Turn this change down"}
          </DialogTitle>
          <DialogDescription>
            {item?.label} · recorded by {item?.by}. {approve
              ? "Once verified it becomes part of the register and the review packs."
              : "Nothing will change in the register."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="label-caps" htmlFor="decision-note">
            A line for the trail (optional)
          </label>
          <Textarea
            id="decision-note"
            data-testid="decision-note"
            rows={3}
            className="rounded-none"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="rounded-none font-display text-xs font-bold uppercase tracking-widest"
            data-testid="decision-confirm"
            disabled={busy}
            onClick={go}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {approve ? "Verify" : "Turn down"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DesignateDialog = ({ open, onOpenChange, plant, onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", designation: "", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", email: "", designation: "", reason: "" });
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/designate", form);
      toast.success(
        data.email_sent
          ? "Sent to SDTD for approval. They will be told by e-mail."
          : "Recorded for SDTD approval. The notice e-mail could not go out.",
      );
      onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card sm:max-w-lg" data-testid="designate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-bold">
            Designate a member for data entry
          </DialogTitle>
          <DialogDescription>
            They will fill {plant} data on your behalf. Every change they make waits for your
            verification and stays out of the Ministry and SteerCo packs until you clear it. SDTD has to
            approve the person before they can sign in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="label-caps" htmlFor="designate-name">Name</label>
            <Input
              id="designate-name"
              data-testid="designate-name"
              className="mt-1 rounded-none"
              value={form.name}
              onChange={set("name")}
              placeholder="Officer's full name"
            />
          </div>
          <div>
            <label className="label-caps" htmlFor="designate-email">SAIL e-mail</label>
            <Input
              id="designate-email"
              data-testid="designate-email"
              className="mt-1 rounded-none"
              value={form.email}
              onChange={set("email")}
              placeholder="name@sail.in"
            />
          </div>
          <div>
            <label className="label-caps" htmlFor="designate-designation">Designation</label>
            <Input
              id="designate-designation"
              data-testid="designate-designation"
              className="mt-1 rounded-none"
              value={form.designation}
              onChange={set("designation")}
              placeholder="e.g. E3 — Assistant Manager"
            />
          </div>
          <div>
            <label className="label-caps" htmlFor="designate-reason">Why (for SDTD)</label>
            <Textarea
              id="designate-reason"
              data-testid="designate-reason"
              rows={2}
              className="mt-1 rounded-none"
              value={form.reason}
              onChange={set("reason")}
              placeholder="e.g. handles the SMS-2 trial readings day to day"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="rounded-none font-display text-xs font-bold uppercase tracking-widest"
            data-testid="designate-submit"
            disabled={busy || !form.name.trim() || !form.email.trim()}
            onClick={go}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send to SDTD
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Verification() {
  const { isDesignated, isAdmin, superAdmin, user, isPartner, isPartnerManager, shows, writable } =
    useAuth();
  const [queue, setQueue] = useState(null);
  const [people, setPeople] = useState(null);
  const [decision, setDecision] = useState(null);
  const [designate, setDesignate] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = async () => {
    const { data } = await api.get("/pending", { params: { status: "" } });
    setQueue(data);
    // Designating a member is a unit matter — a partner has no business with that list.
    if (isPartner) return setPeople(null);
    try {
      const res = await api.get("/users/designations");
      setPeople(res.data);
    } catch {
      setPeople(null);
    }
  };

  // Somebody else's approval clears the queue too, so the page keeps itself current.
  useEffect(() => {
    const timer = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    load();
  }, []);

  const takeBack = async (item) => {
    setBusyId(item.id);
    try {
      await api.post(`/pending/${item.id}/withdraw`);
      toast.success("Taken back — it has left the queue and nothing was written to the register.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const removeItem = async () => {
    const item = removing;
    setRemoving(null);
    try {
      await api.delete(`/pending/${item.id}`);
      toast.success("Off the queue for good. Nothing was recorded.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const decide = async (member, approve) => {
    setBusyId(member.id);
    try {
      await api.post(`/users/${member.id}/decide-designation`, {
        decision: approve ? "approve" : "reject",
      });
      toast.success(
        approve
          ? `${member.name} can now fill ${member.plant} data — the passcode has gone out.`
          : `${member.name} has not been given access.`,
      );
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyId(null);
    }
  };

  if (!queue)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const awaiting = (queue.items || []).filter((i) => i.status === "awaiting");
  const settled = (queue.items || []).filter((i) => i.status !== "awaiting");

  return (
    <div className="p-5 sm:p-8" data-testid="verification-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-3xl">
          <div className="label-caps text-primary">
            {isPartner ? "Pravartanam Partner approval" : "SPOC approval"}
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            {isPartnerManager
              ? "Waiting for you"
              : isDesignated || isPartner
                ? "What you have sent for approval"
                : "SPOC approval pending"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isPartnerManager
              ? "Everything the Pravartanam partners fill waits here for you — use cases, compliance readings and the workbooks they bring in. Nothing has touched the register yet, and none of it goes into the Ministry or SteerCo packs until you clear it. Your own work needs no approval."
              : isPartner
                ? "Everything you fill is kept here until the Pravartanam Partner Manager clears it. Until then the register and the review packs keep reading the figures already approved. The unit's SPOC can see it waiting too."
                : isDesignated
                  ? "Everything you fill is kept here until your SPOC approves it. Until then the register and the review packs keep reading the figures already approved."
                  : "Every change and every data file brought in by anyone other than the unit's own SPOC waits here. A Pravartanam partner's work is cleared by the Partner Manager — you can see it, and chase it. Nothing on this page has touched the register yet."}
          </p>
          {isAdmin && !superAdmin && (
            <p
              className="mt-3 border border-border bg-muted p-3 text-xs text-muted-foreground"
              data-testid="admin-cannot-approve"
            >
              Approval belongs to the unit's own SPOC. SDTD can see everything waiting across the
              units and chase it, but cannot approve or turn down a unit's data on its behalf.
            </p>
          )}
        </div>
        {people?.can_designate && (
          <Button
            className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            data-testid="open-designate"
            onClick={() => setDesignate(true)}
          >
            <UserPlus className="mr-2 h-4 w-4" /> Designate a member
          </Button>
        )}
        <ExportButton
          path="/pending/export"
          params={{ status: "" }}
          testId="export-pending"
          label="Download this queue"
        />
      </div>

      <div className="mt-8 grid gap-px bg-border sm:grid-cols-3" data-testid="verification-kpis">
        {[
          [isPartner ? "Waiting for the Partner Manager" : "Awaiting SPOC approval",
           awaiting.length],
          ["Cleared or turned down", settled.length],
          [isPartner ? "Pravartanam Partner Managers" : "Designated members",
           isPartner ? (queue.partner_managers ? queue.partner_managers.split(", ").length : 0)
                     : (people?.members?.length ?? 0)],
        ].map(([label, value]) => (
          <div key={label} className="bg-card px-5 py-4">
            <div className="font-data text-2xl font-bold">{value}</div>
            <div className="font-data mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
              {label}
            </div>
          </div>
        ))}
      </div>

      {isAdmin && people?.members?.some((m) => m.status === "pending_approval") && (
        <div className="mt-8" data-testid="designation-approvals">
          <h2 className="font-display text-base font-bold uppercase tracking-widest text-muted-foreground md:text-lg">
            SPOC requests waiting for SDTD
          </h2>
          <div className="mt-3 divide-y divide-border border border-border bg-card">
            {people.members
              .filter((m) => m.status === "pending_approval")
              .map((m) => (
                <div
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-4 px-5 py-4"
                  data-testid={`designation-${m.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">
                      {m.name} <span className="text-muted-foreground">· {m.plant}</span>
                    </div>
                    <div className="font-data text-[11px] text-muted-foreground">
                      {m.email}
                      {m.designation ? ` · ${m.designation}` : ""} · asked by{" "}
                      {m.designated_by || "SPOC"}
                    </div>
                    {m.designation_reason && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        “{m.designation_reason}”
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="h-9 rounded-none"
                      data-testid={`reject-designation-${m.id}`}
                      disabled={busyId === m.id}
                      onClick={() => decide(m, false)}
                    >
                      <XCircle className="mr-2 h-4 w-4" /> Refuse
                    </Button>
                    <Button
                      className="h-9 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                      data-testid={`approve-designation-${m.id}`}
                      disabled={busyId === m.id}
                      onClick={() => decide(m, true)}
                    >
                      {busyId === m.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                      )}
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-display text-base font-bold uppercase tracking-widest text-muted-foreground md:text-lg">
          The queue
        </h2>
        <div className="mt-3 divide-y divide-border border border-border bg-card" data-testid="pending-list">
          {awaiting.length === 0 && (
            <p className="px-5 py-8 text-sm text-muted-foreground" data-testid="pending-empty">
              Nothing is waiting. Every change filled in so far has been verified.
            </p>
          )}
          {awaiting.map((item) => (
            <div key={item.id} className="px-5 py-4" data-testid={`pending-${item.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <ShieldQuestion className="h-4 w-4" style={{ color: STATUS_TONE.awaiting }} />
                    <span className="text-sm font-semibold">{item.label}</span>
                    <Chip value={item.status} testId={`pending-status-${item.id}`} />
                    <span className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                      {item.entity_label} · {item.plant}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {item.by} recorded {item.summary} · {fmtWhen(item.at)}
                  </p>
                  {(item.changes || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {item.changes.map((c, i) => (
                        <div key={i} className="font-data text-[11px]">
                          <span className="text-muted-foreground">{c.label}: </span>
                          <span className="line-through opacity-60">{String(c.from ?? "blank")}</span>
                          <span className="mx-1.5">→</span>
                          <span className="font-bold">{String(c.to ?? "blank")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {!item.can_decide && (
                    <p
                      className="mt-2 border-l-2 border-[hsl(36_92%_44%)] pl-2 text-[11px] text-muted-foreground"
                      data-testid={`awaits-spoc-${item.id}`}
                    >
                      {item.partner_queue
                        ? item.unit_spoc && item.unit_spoc !== "no Partner Manager appointed yet"
                          ? `Only a Pravartanam Partner Manager — ${item.unit_spoc} — can approve this.`
                          : "No Pravartanam Partner Manager has been appointed yet. Appoint one on the Users screen and this becomes theirs to clear."
                        : item.unit_spoc
                          ? `Only the ${item.plant} SPOC — ${item.unit_spoc} — can approve this.`
                          : `${item.plant} has no SPOC on the portal right now. Appoint one on the Users screen and this becomes theirs to clear.`}
                    </p>
                  )}
                </div>
                {(item.can_decide || superAdmin || (item.by_email || "").toLowerCase() === (user?.email || "").toLowerCase()) && (
                  <div className="flex shrink-0 items-center gap-2">
                    {superAdmin && (
                      <Button
                        variant="outline"
                        className="h-9 rounded-none border-[hsl(var(--at-risk))] text-[11px] font-bold uppercase tracking-widest text-[hsl(var(--at-risk))]"
                        data-testid={`remove-pending-${item.id}`}
                        onClick={() => setRemoving(item)}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" /> Remove
                      </Button>
                    )}
                    {(item.by_email || "").toLowerCase() === (user?.email || "").toLowerCase() && (
                      <Button
                        variant="ghost"
                        className="h-9 rounded-none text-[11px] font-bold uppercase tracking-widest"
                        data-testid={`withdraw-${item.id}`}
                        onClick={() => takeBack(item)}
                      >
                        Take it back
                      </Button>
                    )}
                    {item.can_decide && shows("verification.approve") && (
                      <>
                        <Button
                          variant="outline"
                          className="h-9 rounded-none"
                          data-testid={`reject-${item.id}`}
                          onClick={() => setDecision({ item, approve: false })}
                        >
                          <XCircle className="mr-2 h-4 w-4" /> Turn down
                        </Button>
                        <Button
                          className="h-9 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                          data-testid={`verify-${item.id}`}
                          onClick={() => setDecision({ item, approve: true })}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" /> Approve
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {people?.members?.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-base font-bold uppercase tracking-widest text-muted-foreground md:text-lg">
            {isAdmin ? "Designated members across the units" : "Members you have designated"}
          </h2>
          <div className="mt-3 divide-y divide-border border border-border bg-card" data-testid="designated-members">
            {people.members.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {m.name} <span className="text-muted-foreground">· {m.plant}</span>
                  </div>
                  <div className="font-data text-[11px] text-muted-foreground">{m.email}</div>
                </div>
                <Chip value={m.status} testId={`member-status-${m.id}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {settled.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-base font-bold uppercase tracking-widest text-muted-foreground md:text-lg">
            Already decided
          </h2>
          <div className="mt-3 divide-y divide-border border border-border bg-card" data-testid="settled-list">
            {settled.slice(0, 30).map((item) => (
              <div key={item.id} className="px-5 py-3" data-testid={`settled-${item.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{item.label}</span>
                  <Chip value={item.status} />
                  <span className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    {item.entity_label} · {item.plant}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {item.by} recorded {item.summary} · {item.status} by {item.decided_by} ·{" "}
                  {fmtWhen(item.decided_at)}
                  {item.note ? ` · “${item.note}”` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {decision && (
        <DecisionDialog
          open={!!decision}
          onOpenChange={(v) => !v && setDecision(null)}
          item={decision.item}
          approve={decision.approve}
          onDone={load}
        />
      )}
      <DesignateDialog
        open={designate}
        onOpenChange={setDesignate}
        plant={user?.plant}
        onDone={load}
      />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title="Take this item off the queue?"
        description={
          removing
            ? `${removing.label} — recorded by ${removing.by}. It will be gone for good, the register stays as it is, and nothing is recorded about the removal.`
            : ""
        }
        confirmLabel="Yes, remove it"
        cancelLabel="No, keep it"
        tone="danger"
        testId="confirm-remove-pending"
        onConfirm={removeItem}
      />
    </div>
  );
}
